import { AdminLayout } from "../../components/layout/AdminLayout";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { FileText, DollarSign, Download, RefreshCw, CreditCard, Clock, AlertTriangle, CheckCircle, Filter } from "lucide-react";
import api from "../../lib/api";


const statusConfig = {
  paid: { color: "#0E8A55", bg: "#D4F2E7", icon: CheckCircle },
  pending: { color: "#D97706", bg: "#FEF6E4", icon: Clock },
  overdue: { color: "#C7322A", bg: "#FDE8E7", icon: AlertTriangle },
};

const InvoicesPage = () => {
  const { token } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [retrying, setRetrying] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const params = filterStatus ? `?status=${filterStatus}` : "";
      const res = await api.get("/invoices${params}");
      setInvoices(res.data.invoices || []);
      setStats(res.data.stats || {});
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, filterStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const retryPayment = async (id) => {
    setRetrying(id);
    try {
      await api.post("/invoices/${id}/retry", {});
      toast.success("Payment processed successfully");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Payment failed"); }
    setRetrying(null);
  };

  const downloadPdf = async (id) => {
    try {
      const res = await api.get("/invoices/${id}/pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Invoice downloaded");
    } catch (e) { toast.error("Failed to download"); }
  };

  if (loading) return <div className="p-6 space-y-4">{[1,2,3].map(i => <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />)}</div>;

  return (
    <AdminLayout title="Invoices">
    <div className="p-6 space-y-6" data-testid="invoices-page">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>Invoices</h1>
        <p className="text-sm text-muted-foreground mt-1">View, download, and manage billing invoices</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        {[
          { label: "Total", value: stats.total || 0, color: "#1B4F8A" },
          { label: "Paid", value: stats.paid || 0, color: "#0E8A55" },
          { label: "Pending", value: stats.pending || 0, color: "#D97706" },
          { label: "Overdue", value: stats.overdue || 0, color: "#C7322A" },
          { label: "Revenue", value: `$${(stats.total_revenue || 0).toLocaleString()}`, color: "#7C3AED" },
          { label: "Outstanding", value: `$${(stats.outstanding || 0).toLocaleString()}`, color: "#F5841F" },
        ].map((s, i) => (
          <div key={i} className="bg-background border rounded-xl p-3" style={{ borderTop: `3px solid ${s.color}` }}>
            <p className="text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">{s.label}</p>
            <p className="text-xl font-extrabold mt-1" style={{ fontFamily: "'Sora', sans-serif" }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex gap-3 items-center">
        <Filter className="w-4 h-4 text-muted-foreground" />
        <select className="h-9 rounded-md border px-3 text-sm bg-background" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} data-testid="invoice-status-filter">
          <option value="">All Status</option>
          <option value="paid">Paid</option>
          <option value="pending">Pending</option>
          <option value="overdue">Overdue</option>
        </select>
      </div>

      {/* Table */}
      <div className="border rounded-xl overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50">
              <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Invoice</th>
              <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Date</th>
              <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Description</th>
              <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Amount</th>
              <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Status</th>
              <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Payment</th>
              <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map(inv => {
              const sc = statusConfig[inv.status] || statusConfig.pending;
              const StatusIcon = sc.icon;
              return (
                <tr key={inv.id} className="border-t hover:bg-muted/30 transition-colors" data-testid={`invoice-row-${inv.id}`}>
                  <td className="p-3 font-mono font-bold text-sm">{inv.id}</td>
                  <td className="p-3 text-muted-foreground">{inv.created_at?.substring(0, 10)}</td>
                  <td className="p-3">{inv.description}</td>
                  <td className="p-3 font-bold">${inv.amount?.toFixed(2)}</td>
                  <td className="p-3">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase" style={{ background: sc.bg, color: sc.color }}>
                      <StatusIcon className="w-3 h-3" />{inv.status}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">{inv.payment_method || "-"}</td>
                  <td className="p-3 flex gap-1">
                    <Button variant="ghost" size="sm" className="h-8" onClick={() => downloadPdf(inv.id)} data-testid={`download-inv-${inv.id}`}>
                      <Download className="w-4 h-4" />
                    </Button>
                    {inv.status !== "paid" && (
                      <Button variant="ghost" size="sm" className="h-8 text-orange-600" onClick={() => retryPayment(inv.id)} disabled={retrying === inv.id} data-testid={`retry-inv-${inv.id}`}>
                        <RefreshCw className={`w-4 h-4 ${retrying === inv.id ? 'animate-spin' : ''}`} />
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
    </AdminLayout>
  );
};

export default InvoicesPage;
